import { useEffect, useMemo, useState, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import api, { API as API_BASE } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { formatDateTime, formatNum } from "../lib/nutrition";
import {
  Plus, MagnifyingGlass, PencilSimple, Trash, DownloadSimple, UploadSimple, FileCsv,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "../components/ui/dropdown-menu";

async function downloadAuthed(url, filename) {
  const token = localStorage.getItem("nc_token");
  const res = await fetch(url, {
    credentials: "include",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) throw new Error("Download fehlgeschlagen");
  const blob = await res.blob();
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(a.href);
}

export default function Ingredients() {
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState([]);
  const [allergenLabels, setAllergenLabels] = useState({});
  const [q, setQ] = useState("");
  const [delId, setDelId] = useState(null);

  const load = useCallback(async () => {
    try {
      const [a, b, c] = await Promise.all([
        api.get("/ingredients"),
        api.get("/meta/nutrients"),
        api.get("/meta/allergens"),
      ]);
      setItems(a.data);
      setMeta(b.data);
      const lbl = {};
      c.data.forEach((x) => { lbl[x.key] = x.label; });
      setAllergenLabels(lbl);
    } catch (err) {
      console.error("Fehler beim Laden der Zutaten:", err);
      toast.error("Zutaten konnten nicht geladen werden");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return items;
    return items.filter((i) =>
      i.name.toLowerCase().includes(s) ||
      (i.category || "").toLowerCase().includes(s));
  }, [items, q]);

  const handleDelete = async () => {
    if (!delId) return;
    try {
      await api.delete(`/ingredients/${delId}`);
      toast.success("Zutat gelöscht");
      setDelId(null);
      load();
    } catch {
      toast.error("Löschen fehlgeschlagen");
    }
  };

  const fileInputRef = useRef(null);
  const [csvBusy, setCsvBusy] = useState(false);

  const handleExportCsv = async () => {
    try {
      await downloadAuthed(`${API_BASE}/ingredients-csv/export`, "nutricalc_zutaten.csv");
      toast.success("CSV heruntergeladen");
    } catch {
      toast.error("Export fehlgeschlagen");
    }
  };

  const handleTemplate = async () => {
    try {
      await downloadAuthed(`${API_BASE}/ingredients-csv/template`, "nutricalc_zutaten_vorlage.csv");
      toast.success("Vorlage heruntergeladen");
    } catch {
      toast.error("Vorlage konnte nicht geladen werden");
    }
  };

  const handleImportClick = () => fileInputRef.current?.click();

  const handleImportFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setCsvBusy(true);
    try {
      const form = new FormData();
      form.append("file", f);
      const { data } = await api.post("/ingredients-csv/import", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const errs = (data.errors || []).length;
      toast.success(
        `Import: ${data.created} neu · ${data.updated} aktualisiert${errs ? ` · ${errs} Fehler` : ""}`
      );
      load();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Import fehlgeschlagen";
      toast.error(typeof msg === "string" ? msg : "Import fehlgeschlagen");
    } finally {
      setCsvBusy(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  return (
    <div className="space-y-6" data-testid="ingredients-page">
      <header className="flex flex-col sm:flex-row sm:flex-wrap sm:items-end sm:justify-between gap-3 sm:gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-zinc-500 mb-1">Datenbank</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight">Zutaten</h1>
          <p className="text-sm text-zinc-500 mt-1">Nährwerte pro 100&nbsp;g nach EU VO 1169/2011.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 sm:flex-none min-w-0">
            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <Input
              placeholder="Suchen…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="pl-9 w-full sm:w-64 rounded-sm"
              data-testid="ingredient-search-input"
            />
          </div>
          <Link to="/zutaten/neu" className="shrink-0">
            <Button className="bg-[#002FA7] hover:bg-[#00248a] rounded-sm" data-testid="new-ingredient-button">
              <Plus size={14} className="mr-2" /> Neue Zutat
            </Button>
          </Link>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="rounded-sm shrink-0" data-testid="csv-menu-button" disabled={csvBusy}>
                <FileCsv size={14} className="mr-2" />
                CSV
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem onClick={handleExportCsv} data-testid="csv-export-action">
                <DownloadSimple size={14} className="mr-2" /> Alle Zutaten exportieren
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleTemplate} data-testid="csv-template-action">
                <DownloadSimple size={14} className="mr-2" /> Leere Vorlage laden
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleImportClick} data-testid="csv-import-action">
                <UploadSimple size={14} className="mr-2" /> Aus CSV importieren …
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,text/csv"
            className="hidden"
            onChange={handleImportFile}
            data-testid="csv-file-input"
          />
        </div>
      </header>

      <div className="border border-zinc-200 rounded-sm overflow-hidden">
        <div className="overflow-x-auto max-h-[70vh]">
          <table className="nutri-table w-full text-sm">
            <thead>
              <tr>
                <th className="sticky-col min-w-[220px]">Zutat</th>
                <th>Kategorie</th>
                <th className="min-w-[160px]">Allergene</th>
                {meta.map((m) => (
                  <th key={m.key} className="text-right">
                    <div className="leading-tight">{m.label}</div>
                    <div className="text-[10px] font-normal text-zinc-400">{m.unit}/100g</div>
                  </th>
                ))}
                <th>Erstellt</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={meta.length + 5} className="px-6 py-12 text-center text-zinc-500" data-testid="empty-ingredients">
                    Keine Zutaten gefunden. <Link to="/zutaten/neu" className="text-[#002FA7] underline">Erste anlegen</Link>.
                  </td>
                </tr>
              ) : filtered.map((i) => (
                <tr key={i.id} data-testid={`ingredient-row-${i.id}`}>
                  <td className="sticky-col font-medium">
                    <Link to={`/zutaten/${i.id}`} className="hover:text-[#002FA7]">{i.name}</Link>
                  </td>
                  <td className="text-zinc-500">{i.category || "—"}</td>
                  <td>
                    {(i.allergens || []).length === 0 ? (
                      <span className="text-zinc-300 text-xs">—</span>
                    ) : (
                      <div className="flex flex-wrap gap-1">
                        {(i.allergens || []).slice(0, 3).map((k) => (
                          <span key={k}
                            className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-medium rounded-sm bg-amber-100 text-amber-900 border border-amber-200">
                            {allergenLabels[k] || k}
                          </span>
                        ))}
                        {(i.allergens || []).length > 3 && (
                          <span className="text-[10px] text-zinc-500">+{i.allergens.length - 3}</span>
                        )}
                      </div>
                    )}
                  </td>
                  {meta.map((m) => (
                    <td key={m.key} className="text-right font-mono-num">
                      {formatNum(i.nutrients?.[m.key] || 0)}
                    </td>
                  ))}
                  <td className="text-zinc-500 text-xs">
                    <div>{i.created_by_name}</div>
                    <div className="font-mono-num text-[11px] text-zinc-400">{formatDateTime(i.created_at)}</div>
                  </td>
                  <td className="text-right">
                    <div className="flex justify-end gap-1">
                      <Link to={`/zutaten/${i.id}`}>
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0" data-testid={`edit-ingredient-${i.id}`}>
                          <PencilSimple size={14} />
                        </Button>
                      </Link>
                      <Button
                        size="sm" variant="ghost"
                        className="h-7 w-7 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={() => setDelId(i.id)}
                        data-testid={`delete-ingredient-${i.id}`}
                      >
                        <Trash size={14} />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <AlertDialog open={!!delId} onOpenChange={(o) => !o && setDelId(null)}>
        <AlertDialogContent data-testid="delete-ingredient-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Zutat löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              Diese Aktion kann nicht rückgängig gemacht werden. Rezepte mit dieser Zutat behalten zwar den Eintrag, aber die Nährwertberechnung schließt sie aus.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-ingredient">
              Löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
